'use client'

import { useState, useEffect } from 'react'

/**
 * Hook pour debouncer une valeur
 * Utile pour les champs de recherche : évite de filtrer à chaque frappe
 * 
 * @param value - La valeur à debouncer
 * @param delay - Délai en ms (par défaut 300ms)
 * @returns La valeur debouncée
 * 
 * @example
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebouncedValue(search, 300)
 * 
 * // Filtrage utilise debouncedSearch au lieu de search
 * const filtered = items.filter(item => 
 *   item.name.includes(debouncedSearch)
 * )
 */
export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Créer un timer qui met à jour la valeur après le délai
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Nettoyer le timer si value change avant la fin du délai
    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

